import React, {
  Dispatch,
  FC,
  MouseEvent,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertColor, Box, Button, Grid, IconButton, Modal, Stack, Typography } from '@mui/material';
import strings from 'common/Translation/Translate';
import DrawerMenu from 'components/DrawerMenu';
import Form from 'components/Form';
import PageLoader from 'components/PageLoader';
import Section from 'components/Section/Section';
import { SendCorporateRateForm } from './SendCorporateRateForm';
import axiosInstance from '../../../../../utils/axios';
import { URLS } from '../../../../../utils/constants/urls';
import { createDropdownData, removeErrorFieldsFromValues } from '../../../../../utils/validators/HelperFunctions';
import CloseIcon from '@mui/icons-material/Close';

type SendCorporateRateProps = {
  companyId: number;
  rateId: number;
  setRateId: (id: number | null) => void;
  setMessage: Dispatch<
    SetStateAction<{
      display: boolean;
      severity: AlertColor | null;
      message: any;
    }>
  >;
  openDrawer: boolean;
  setOpenDrawer: (val: boolean) => void;
};

const SendCorporateRate: FC<SendCorporateRateProps> = ({
  companyId,
  rateId,
  setRateId,
  setMessage,
  openDrawer,
  setOpenDrawer,
}) => {
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [contacts, setContacts] = useState<{ label: string; value: string }[]>([]);
  const [mappedOrganizations, setMappedOrganizations] = useState<{ label: string; value: string }[]>([]);
  const [showPreviewModal, setShowPreviewModal] = useState<boolean>(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [organizationId, setOrganizationId] = useState<number | null>(null);

  let formRef: RefObject<Form | null | undefined> = useRef();
  /** Sync guard so a second Send click in the same tick cannot start another POST. */
  const savingRef = useRef(false);
  const [isSending, setIsSending] = useState(false);

  const handleCatch = useCallback(
    (error: any) => {
      setMessage({
        display: true,
        severity: 'error',
        message: error.response?.data?.error_description ?? error.response?.data?.message ?? error.message,
      });
    },
    [setMessage],
  );

  const fetchContacts = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);

    try {
      const { status, data } = await axiosInstance.get(URLS.getCorporateRatesDropdown(companyId));
      if (status === 200) {
        setContacts(createDropdownData(data.contacts, ['id', 'name']));
        setMappedOrganizations(createDropdownData(data.organizations, ['id', 'name']));
        if (data.organizations.length === 1) {
          setOrganizationId(data.organizations[0].value);
        }
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, handleCatch]);

  useEffect(() => {
    (async () => {
      await fetchContacts();
    })();
  }, [fetchContacts]);

  const closeDrawer = useCallback(() => {
    formRef.current?.resetForm();
    setOpenDrawer(false);
    setRateId(null);

    setPreviewHtml('');
    setPageResponse(null);
    setShowPreviewModal(false);
  }, [setOpenDrawer, setRateId]);

  const closePreviewModal = useCallback(() => {
    formRef.current?.resetForm();

    setPreviewHtml('');
    setPageResponse(null);
    setShowPreviewModal(false);
  }, [setRateId]);

  const handleSave = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (savingRef.current || isLoading) return;
    savingRef.current = true;
    setIsSending(true);

    try {
      const data = formRef.current?.getFormData();

      const isFormValid = data?.isFormValid;
      if (!isFormValid) {
        setHasError(true);
        return;
      }

      const cleanedData = removeErrorFieldsFromValues(data?.formData);

      const formData = new FormData();
      formData.append('to', cleanedData.to.map((recipient: any) => recipient.label).join(','));
      formData.append('cc', cleanedData.cc);
      formData.append('bcc', cleanedData.bcc);
      if (mappedOrganizations.length > 1) {
        formData.append('organization', cleanedData.organization.value);
      }
      if (cleanedData?.attachments === '' || cleanedData?.attachments?.length === 0) {
        formData.append('attachments', [] as any);
      } else {
        cleanedData?.attachments?.forEach((file: any) => {
          formData.append('attachments', file);
        });
      }

      setIsLoading(true);
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      const { status } = await axiosInstance.post(URLS.sendCorporateRatesEmail(companyId, rateId), formData, config);
      if (status === 200) {
        setMessage({
          display: true,
          severity: 'success',
          message: strings.sales_corporate_rate_email_success,
        });
        setOpenDrawer(false);
        setHasError(false);
      }
    } catch (error: any) {
      handleCatch(error);
    } finally {
      savingRef.current = false;
      setIsSending(false);
      setIsLoading(false);
    }
  };

  const fetchPreview = useCallback(async () => {
    if (showPreviewModal) {
      try {
        setIsLoading(true);
        const params = organizationId && { organization: organizationId };

        const { data } = await axiosInstance.post(URLS.previewCorporateRates(companyId, rateId), {}, { params });
        setPreviewHtml(data); // store HTML string
      } catch (error) {
        handleCatch(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [showPreviewModal, rateId, organizationId, companyId, handleCatch]);

  useEffect(() => {
    (async () => {
      await fetchPreview();
    })();
  }, [fetchPreview]);

  const previewModal = useMemo(() => {
    return (
      <Modal
        open={showPreviewModal}
        onClose={closePreviewModal}
        aria-labelledby="preview-modal-title"
        aria-describedby="preview-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 3,
            p: 1,
            width: { xs: '90vw', sm: '80vw', md: '65vw' },
            maxHeight: '90vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
          {/* Header */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              borderBottom: '1px solid #eee',
              px: 3,
              py: 2,
              bgcolor: 'grey.100',
            }}>
            {isLoading && <PageLoader />}

            <Typography id="preview-modal-title" variant="h6" fontWeight={600}>
              {strings.bookings_button_preview}
            </Typography>
            <IconButton
              onClick={closePreviewModal}
              size="small"
              aria-label="Close preview"
              sx={{
                color: 'grey.600',
                '&:hover': {
                  color: 'grey.800',
                  bgcolor: 'grey.200',
                },
              }}>
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Content */}
          <Box
            id="preview-modal-description"
            sx={{
              p: 3,
              overflowY: 'auto',
              flexGrow: 1,
            }}>
            <Typography variant={'body2'} sx={{ p: 0 }} dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </Box>
        </Box>
      </Modal>
    );
  }, [isLoading, previewHtml, showPreviewModal, closePreviewModal]);

  const onChange = async (field: string, value: any) => {
    if (field === 'organization') {
      setOrganizationId(value?.value);
    }
  };

  return (
    <>
      <Section />
      {isLoading && <PageLoader />}
      {showPreviewModal && previewModal}

      <DrawerMenu
        width={'50vw'}
        open={openDrawer}
        handleClose={closeDrawer}
        closeButton={true}
        title={strings.sales_corporate_rate_email}>
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Form
            hasError={hasError}
            ref={formRef as RefObject<Form>}
            model={SendCorporateRateForm(strings, contacts, mappedOrganizations)}
            values={pageResponse}
            onChange={onChange}
          />
        </Grid>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          <Button
            variant="contained"
            onClick={e => handleSave(e)}
            size="medium"
            disabled={isLoading || isSending}
            sx={{ textTransform: 'capitalize', mb: 1 }}>
            {strings.bookings_text_send_email}
          </Button>
          <Button
            variant="outlined"
            onClick={() => setShowPreviewModal(true)}
            size="medium"
            disabled={mappedOrganizations.length > 0 && organizationId === null}
            sx={{ textTransform: 'capitalize', mb: 1 }}>
            {strings.bookings_button_preview}
          </Button>
        </Stack>
      </DrawerMenu>
    </>
  );
};
export default SendCorporateRate;
